import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scoreService, ScoreInsertRequest, ScoreInsertDTO } from '../../services/scoreService';
import { classeService, Classe } from '../../services/classeService';
import { courseService, Course } from '../../services/courseService';
import { studentService, Student } from '../../services/studentService';
import './ScoreInsert.css';

const ScoreInsert: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const classId = searchParams.get('classId') || '';
  const courseId = searchParams.get('courseId') || '';
  const examDate = searchParams.get('examDate') || '';
  
  const [classe, setClasse] = useState<Classe | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [scores, setScores] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!classId || !courseId || !examDate) {
      navigate('/classes');
      return;
    }
    
    loadData();
  }, [classId, courseId, examDate]);

  const loadData = async () => {
    try {
      const [classeData, courseData, studentsResponse] = await Promise.all([
        classeService.getClasseById(classId),
        courseService.getCourseById(courseId),
        studentService.searchStudents({
          page: 0,
          size: 1000,
          filters: { classId },
        }),
      ]);
      
      setClasse(classeData);
      setCourse(courseData);
      setStudents(studentsResponse.content);
      setFilteredStudents(studentsResponse.content);
      
      // Initialize scores map with 0 for all students
      const initialScores = new Map<string, number>();
      studentsResponse.content.forEach(student => {
        initialScores.set(student.id!, 0);
      });
      setScores(initialScores);
    } catch (e: any) {
      setError(t('scores.failedToLoadData'));
      console.error('Failed to load data:', e);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredStudents(students);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = students.filter(student => 
      student.firstName?.toLowerCase().includes(lowerQuery) ||
      student.lastName?.toLowerCase().includes(lowerQuery) ||
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(lowerQuery)
    );
    
    setFilteredStudents(filtered);
  };

  const handleScoreChange = (studentId: string, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 20) return;
    
    setScores(new Map(scores.set(studentId, numValue)));
  };

  const handleSave = async () => {
    // Build request
    const scoreList: ScoreInsertDTO[] = Array.from(scores.entries()).map(([studentId, score]) => ({
      studentId,
      score,
    }));

    const request: ScoreInsertRequest = {
      classId,
      courseId,
      examDate: new Date(examDate).toISOString(),
      scores: scoreList,
    };

    try {
      setLoading(true);
      setError('');
      await scoreService.insertScoresBatch(request);
      setSuccess(t('scores.scoresInsertedSuccessfully'));
      setTimeout(() => {
        navigate('/classes');
      }, 2000);
    } catch (e: any) {
      setError(e.response?.data?.message || t('scores.failedToInsertScores'));
      console.error('Insert scores error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/classes');
  };

  return (
    <div className="score-insert-container">
      <div className="score-insert-header">
        <div>
          <h2>{t('scores.insertScores')}</h2>
          <div className="score-insert-info">
            <span><strong>{t('scores.class')}:</strong> {classe?.name}</span>
            <span><strong>{t('scores.course')}:</strong> {course?.name}</span>
            <span><strong>{t('scores.examDate')}:</strong> {new Date(examDate).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="score-insert-actions">
          <button onClick={handleCancel} className="btn-cancel" disabled={loading}>
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} className="btn-save" disabled={loading}>
            {loading ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Search Bar */}
      <div className="score-search-container">
        <input
          type="text"
          placeholder={t('scores.searchStudent')}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="score-search-input"
          disabled={loading}
        />
        <span className="score-count">
          {filteredStudents.length} / {students.length} {t('scores.students')}
        </span>
      </div>

      <div className="score-insert-table-wrapper">
        <table className="score-insert-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('scores.student')}</th>
              <th>{t('scores.score')} (0-20)</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student, index) => (
              <tr key={student.id}>
                <td>{index + 1}</td>
                <td>{student.firstName} {student.lastName}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    step="0.5"
                    value={scores.get(student.id!) || 0}
                    onChange={(e) => handleScoreChange(student.id!, e.target.value)}
                    disabled={loading}
                    className="score-input"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredStudents.length === 0 && searchQuery && (
        <div className="no-results-message">
          <p>{t('scores.noStudentsFound')}</p>
        </div>
      )}

      {students.length === 0 && (
        <div className="no-students-message">
          <p>{t('scores.noStudentsInClass')}</p>
        </div>
      )}
    </div>
  );
};

export default ScoreInsert;

